import { fn } from '@ember/helper';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import { t } from 'ember-intl';
import type { IntlService } from 'ember-intl';
import sortableGroup from 'ember-sortable/modifiers/sortable-group';
import { and, eq, or } from 'ember-truth-helpers';
import { trackedFunction } from 'reactiveweb/function';

import Button from 'umg-ember-backend/components/ui/button';
import ContextMenu from 'umg-ember-backend/components/ui/context-menu';
import { getBem } from 'umg-ember-backend/utils/get-bem';

import FormField from './field';
import styles from './styles.module.css';

interface FormFieldsSignature {
  Args: {
    form: TODO;
    showErrors: boolean;
    newRecord: (
      model: TODO,
      relationKind: string,
      relationType: string,
      relationalField: string,
      filters: TODO,
    ) => Promise<void>;
    deleteRelation: (
      field: TODO,
      model: TODO,
      relationKind: string,
      relationalField: string,
    ) => void;
  };
}

const bem = getBem(styles);

export default class FormFieldsComponent extends Component<FormFieldsSignature> {
  // Services
  @service declare intl: IntlService;

  // Defaults
  fieldTypes = [
    'checkbox',
    'divider',
    'email',
    'file',
    'infotext',
    'select',
    'text',
    'textarea',
  ];

  get fieldTypeOptions() {
    return this.fieldTypes.map((type) => {
      return {
        value: type,
        label: this.intl.t(`forms.contest-form-field.options.type.${type}`),
        icon: `form-field-${type}`,
      };
    });
  }

  get hasFieldEmail() {
    if (this.findEmailField.isPending) {
      return true;
    }

    return !!this.findEmailField.value;
  }

  addFormField = async (type: string) => {
    await this.args.newRecord(
      this.args.form.model,
      'has-many',
      'contest-form-field',
      'formFields',
      {
        type,
      },
    );
  };

  reorderFormFields = (fields: TODO) => {
    for (let index = 0; index < fields.length; index++) {
      const field = fields[index];

      field.position = index;
    }
    this.args.form.model.formFields = fields;
  };

  findEmailField = trackedFunction(this, async () => {
    const formFields = await this.args.form.model.formFields;

    return formFields.find((field: TODO) => field.type === 'email');
  });

  // Template
  <template>
    <div class={{(bem)}}>
      <ol
        class={{bem 'form-fields'}}
        {{sortableGroup
          groupName='contest-form-fields'
          onChange=this.reorderFormFields
        }}
      >

        {{#each @form.model.formFields as |field|}}
          <FormField
            @field={{field}}
            @parentModel={{@form.model}}
            @deleteRelation={{@deleteRelation}}
            @showErrors={{@showErrors}}
          />
        {{/each}}
      </ol>
      <ContextMenu @isDisabled={{@form.isDisabled}}>
        <:trigger>
          <Button
            @label={{t 'record_detail.forms.contest.form_fields.add'}}
            @icon='add'
            @style='outline'
            disabled={{@form.isDisabled}}
          />
        </:trigger>

        <:content as |content|>
          {{#each this.fieldTypeOptions as |type|}}
            <content.Item
              @label={{type.label}}
              @icon={{type.icon}}
              @onClick={{fn this.addFormField type.value}}
              @isDisabled={{or
                @form.isDisabled
                (and (eq type.value 'email') this.hasFieldEmail)
              }}
            />
          {{/each}}
        </:content>
      </ContextMenu>
    </div>
  </template>
}
